import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShoppingBag, ArrowLeft, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { firstAllowedPath } from "@/lib/landing";

export default function Login() {
  const { type } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const isAdmin = type !== "user";
  const loginType = type || null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/auth/setup-status")
      .then((r) => { if (r.data.needs_setup) navigate("/setup", { replace: true }); })
      .catch(() => {});
  }, [navigate]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password, loginType);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}!`);
      navigate(firstAllowedPath(res.user), { replace: true });
    } else {
      setError(res.error);
    }
  }

  const accent = isAdmin ? "#1A2A5E" : "#F4571E";

  return (
    <div className="min-h-screen brand-grid-bg flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="back-to-select" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1A2A5E] mb-5" style={{ visibility: type ? "visible" : "hidden" }}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="p-7 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-slate-50 rounded-xl p-1.5 ring-1 ring-slate-100"><Logo size={40} /></div>
              <div>
                <div className="font-display font-extrabold text-lg leading-none"><span className="text-[#1A2A5E]">Bill</span><span className="text-[#F4571E]">One</span></div>
                <div className="text-[8px] tracking-[0.2em] text-slate-400 font-semibold mt-1">BILLING MADE SIMPLE</div>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center" style={{ background: `${accent}15` }}>
                {isAdmin ? <ShieldCheck className="h-6 w-6" style={{ color: accent }} /> : <ShoppingBag className="h-6 w-6" style={{ color: accent }} />}
              </div>
              <div>
                <h1 className="font-display text-xl font-bold text-[#0F172A]">Login</h1>
                <p className="text-xs text-slate-400">{isAdmin ? "Manage products, users, reports & more" : "Quick billing and sales"}</p>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-slate-600">Email or Username</Label>
                <Input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@store.com" className="mt-1.5" autoFocus required />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-slate-600">Password</Label>
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-[#F4571E] hover:underline">Forgot password?</Link>
                </div>
                <div className="relative mt-1.5">
                  <Input data-testid="login-password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && <div data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}

              <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full h-11 text-base font-semibold" style={{ background: accent }}>
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in...</> : `Login`}
              </Button>
            </form>
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">Powered by <span className="font-semibold text-slate-500">ACore</span></p>
      </div>
    </div>
  );
}
